import React, { useState } from 'react';
import { DetailModal } from 'components/DetailModal/DetailModal';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { BenefitDetailContent } from 'components/BenefitDetailContent/BenefitDetailContent';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import type { BenefitDetail } from 'features/resources/types';

interface BenefitDetailPageProps {
  benefit?: BenefitDetail;
  /** Controls whether the modal is visible. */
  open?: boolean;
  /** Dismiss callback. */
  onClose?: () => void;
  /** For Storybook: seeds the leave-app confirmation dialog open on mount. */
  defaultVisitDialogOpen?: boolean;
  /** Called when the user confirms leaving the app. Defaults to opening the benefit URL. */
  onConfirmVisit?: () => void;
}

export const BenefitDetailPage: React.FC<BenefitDetailPageProps> = ({
  benefit,
  open = false,
  onClose = () => {},
  defaultVisitDialogOpen = false,
  onConfirmVisit,
}) => {
  const { translation } = useTranslationContext();
  const { isDesktop } = useDeviceType();
  const [isVisitDialogOpen, setIsVisitDialogOpen] = useState(defaultVisitDialogOpen);

  if (!open && isVisitDialogOpen) {
    setIsVisitDialogOpen(false);
  }

  const showVisitDialog = open && isVisitDialogOpen;

  const openVisitDialog = () => setIsVisitDialogOpen(true);
  const closeVisitDialog = () => setIsVisitDialogOpen(false);

  const handleCloseModal = () => {
    closeVisitDialog();
    onClose();
  };

  const handleConfirmVisit = () => {
    closeVisitDialog();
    if (onConfirmVisit) {
      onConfirmVisit();
    } else if (benefit?.visitUrl) {
      window.open(benefit.visitUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!benefit) {
    return null;
  }

  return (
    <DetailModal
      open={open}
      onClose={handleCloseModal}
      title={translation('benefit_detail_screen')}
      titleId="benefit-detail-title"
      isDialogOpen={showVisitDialog}
    >
      <BenefitDetailContent benefit={benefit} onClickVisit={openVisitDialog} />
      <ConfirmationDialog
        className="benefit-detail-page__visit-dialog"
        open={showVisitDialog}
        onClose={closeVisitDialog}
        title=""
        // No visible title; label the dialog by its description text.
        aria-labelledby="confirmation-dialog-description"
        description={translation('resources_member_benefit_detail_visit_site_message')}
        noButtonLabel={translation('cancel')}
        yesButtonLabel={translation('general_yes')}
        handleNo={closeVisitDialog}
        handleYes={handleConfirmVisit}
        hideBackdrop={isDesktop}
        PaperProps={{ sx: { maxWidth: 320 }, ...(isDesktop && { elevation: 24 }) }}
      />
    </DetailModal>
  );
};
